import React, { Fragment, RefObject } from "react";
import { RouteComponentProps } from "react-router";
import {
  request,
  handleValidationErrors,
  responseHandler,
} from "../helpers/utilities";
import { Spin, Row, Col, message } from "antd";
import Card from "../components/Card";
import {
  Role,
  AUMTarget,
  APIError,
  FormFieldConfig,
} from "../helpers/shared-interfaces";
import BaseForm from "../components/Form/BaseForm";
import { FormInstance } from "antd/lib/form";
import { AxiosResponse } from "axios";
import * as Sentry from "@sentry/browser";
import { CREATE_AUM_TARGET } from "../gql/aum";
import { GET_CURRENT_PERIOD } from "../gql/general";
import { GET_ROLES } from "../gql/roles";
import { generateDisplayErrorMessage } from "../helpers/utilities";


interface State {
  amount: number;
  isLoading: boolean;
  roleId: string;
  roles: Role[];
  currentPeriod: string[];
}

interface RouteParams {
  id?: string;
}

interface Props extends RouteComponentProps<RouteParams> {
  name?: string;
}

enum duplicateError {
  property = "roleId",
  description = "has already been taken",
}

export default class CreateAUMTarget extends React.Component<Props, State> {
  reference: RefObject<FormInstance>;

  constructor(props: Props) {
    super(props);
    this.state = {
      amount: 0,
      roleId: "",
      isLoading: true,
      roles: [],
      currentPeriod: [],
    };
    this.redirectToAllAUMTargetsPage =
      this.redirectToAllAUMTargetsPage.bind(this);
    this.createAUMTarget = this.createAUMTarget.bind(this);
    this.reference = React.createRef<FormInstance>();
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  async createAUMTarget(): Promise<void> {
    const fieldsToBeValidated: string[] = ["roleId", "amount"];

    interface Validated {
      roleId: string;
      amount: number;
    }

    interface Response {
      createAumTarget: {
        aumTarget: AUMTarget;
        errors: APIError[];
      };
    }

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_AUM_TARGET,
        variables: {
          roleId: data.roleId,
          amount: data.amount,
        },
      });

      this.setState({ isLoading: false });

      const { handled, err } = responseHandler(response, "createAumTarget");

      if (handled) {
        message.error("An Error Occurred");
        return;
      } else if (err) {
        for (const error of err) {
          if (
            error.property === duplicateError.property &&
            error.description === duplicateError.description
          ) {
            message.error(`AUM Target for this role exists already - ${generateDisplayErrorMessage(err)}`);
          } else {
            message.error(`Failed to create AUM-Target - ${generateDisplayErrorMessage(err)}`);
          }
        }
      } else {
        message.success("AUM-Target created sucessfully");
        this.redirectToAllAUMTargetsPage();
      }
    } catch (error) {
      this.setState({ isLoading: false });
      Sentry.captureException(error);
    }
  }

  redirectToAllAUMTargetsPage(): void {
    this.props.history.push("/aum-targets");
  }

  getAllRoles(): void {
    request({
      query: GET_ROLES,
      variables: {
        page: 1,
      },
    }).then(({ data: { data } }: any): void => {
      this.setState({
        roles: data.roles.results,
        isLoading: false,
      });
    });
  }

  getCurrentPeriod(): void {
    request({
      query: GET_CURRENT_PERIOD,
    }).then(({ data: { data } }: any): void => {
      this.setState({
        currentPeriod: data.currentPeriod,
      });
    });
  }

  componentDidMount(): void {
    this.setState({ isLoading: true }, (): void => {
      this.getAllRoles();
      this.getCurrentPeriod();
    });
  }

  render(): JSX.Element {
    const { isLoading, roles, currentPeriod } = this.state;

    const aumTargetConfig: FormFieldConfig[] = [
      {
        id: "roleId",
        name: "roleId",
        inputType: "select",
        initialValue: undefined,
        required: true,
        placeholder: "Role",
        rule: "string",
        label: "Role",
        selectOptions: roles,
        formItemStyle: { marginBottom: "0px" },
        elementStyle: {
          width: "80%",
        },
        colConfig: {
          span: 15,
        },
      },
      {
        id: "amount",
        name: "amount",
        inputType: "number",
        initialValue: undefined,
        required: true,
        placeholder: "Amount",
        rule: "number",
        label: "Amount",
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          span: 12,
        },
      },
      {
        id: "createAUMTargetBtn",
        name: "createAUMTargetBtn",
        inputType: "button",
        value: "Create",
        required: false,
        className: "base-button",
        label: "Button",
        handleClick: this.createAUMTarget,
        elementStyle: {
          position: "relative",
          top: "-47px",
        },
        colConfig: {
          span: 24,
        },
      },
    ];

    return (
      <Card header="NEW AUM-Target">
        <Fragment>
          <Row
            style={{
              marginBottom: "12px",
            }}
          >
            <div>
              {currentPeriod.length > 0 && (
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "20px",
                  }}
                >
                  {currentPeriod[0]} - {currentPeriod[11]}
                </span>
              )}
            </div>
          </Row>
          <Row>
            <Col span={8}>
              <div className="create-aum-target-container" style={{}}>
                <Spin spinning={isLoading}>
                  <BaseForm
                    fieldsToHide={[]}
                    reference={this.reference}
                    fields={aumTargetConfig}
                    layout="vertical"
                  />
                </Spin>
              </div>
            </Col>
          </Row>
        </Fragment>
      </Card>
    );
  }
}
